import {FileIcon} from '../FileIcon/FileIcon';
import {Icon} from '../Icon/Icon';
import {ProgressBar} from '../ProgressBar';
import {Button} from '../shadcn/button';

export interface UploadingItemProps {
  fileName: string;
  progress: number;
  uploadedBytesLabel: string;
  uploadingLabel: string;
  onRemove?: () => void;
  isLoading: boolean;
  ext: string;
}

export function UploadingItem({
  fileName,
  progress,
  uploadedBytesLabel,
  uploadingLabel,
  onRemove,
  isLoading,
  ext
}: UploadingItemProps) {
  const percent = Math.min(100, Math.max(0, progress));

  return (
    <div
      data-slot="file-upload-item"
      data-state="uploading"
      className="flex w-full items-start gap-3 rounded-xl border border-border-subtle bg-background-surface p-4"
    >
      {/* File icon — logical-start (left LTR / right RTL) */}
      <FileIcon ext={ext} size="md" />

      {/* Text content — fills remaining space */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Filename */}
        <p className="truncate text-sm font-medium text-fg-secondary">{fileName}</p>

        {/* Status row */}
        <div className="flex items-center gap-2">
          <p className="truncate text-sm text-fg-tertiary">{uploadedBytesLabel}</p>
          <span className="h-3 w-px shrink-0 bg-border" aria-hidden="true" />
          <div className="flex shrink-0 items-center gap-1 text-fg-tertiary">
            <span className="text-sm">{uploadingLabel}</span>
            <Icon name="upload" className="size-4" />
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-3">
          <ProgressBar
            value={percent}
            aria-label={uploadingLabel}
            className="flex-1 border border-border-subtle bg-background-secondary"
          />
          <span className="w-9 shrink-0 text-end text-sm font-medium text-fg-secondary">
            {percent}%
          </span>
        </div>
      </div>

      {/* Delete button — logical-end (right LTR / left RTL) */}
      {onRemove && (
        <Button
          variant="tertiary"
          size="icon-sm"
          onClick={onRemove}
          disabled={isLoading}
          aria-label="Remove file"
        >
          <Icon name="delete" />
        </Button>
      )}
    </div>
  );
}
